"use strict";
/*******************************************************************************

    The class that defines the transaction's outputs of a block.

    Copyright:
        Copyright (c) 2020-2021 BOSAGORA Foundation
        All rights reserved.

    License:
        MIT License. See LICENSE for details.

*******************************************************************************/
Object.defineProperty(exports, "__esModule", { value: true });
exports.TxOutput = exports.OutputType = void 0;
var Amount_1 = require("../common/Amount");
var KeyPair_1 = require("../common/KeyPair");
var Lock_1 = require("../script/Lock");
var JSONValidator_1 = require("../utils/JSONValidator");
var Utils_1 = require("../utils/Utils");
var VarInt_1 = require("../utils/VarInt");
/**
 * The transaction output type constant, 4 Byte unsigned integer
 */
var OutputType;
(function (OutputType) {
    OutputType[OutputType["Payment"] = 0] = "Payment";
    OutputType[OutputType["Freeze"] = 1] = "Freeze";
    OutputType[OutputType["Coinbase"] = 2] = "Coinbase";
})(OutputType = exports.OutputType || (exports.OutputType = {}));
/**
 * The class that defines the transaction's outputs of a block.
 * Convert JSON object to TypeScript's instance.
 * An exception occurs if the required property is not present.
 */
var TxOutput = /** @class */ (function () {
    /**
     * Constructor
     * @param type    The type of the transaction output
     * @param value   The monetary value
     * @param lock    The public key or instance of Lock
     */
    function TxOutput(type, value, lock) {
        this.type = type;
        if (lock instanceof KeyPair_1.PublicKey)
            this.lock = Lock_1.Lock.fromPublicKey(lock);
        else
            this.lock = lock;
        this.value = Amount_1.Amount.make(value);
    }
    /**
     * The reviver parameter to give to `JSON.parse`
     *
     * This function allows to perform any necessary conversion,
     * as well as validation of the final object.
     *
     * @param key   Name of the field being parsed
     * @param value The value associated with `key`
     * @returns A new instance of `TxOutputs` if `key == ""`, `value` otherwise.
     */
    TxOutput.reviver = function (key, value) {
        if (key !== "")
            return value;
        JSONValidator_1.JSONValidator.isValidOtherwiseThrow("TxOutput", value);
        return new TxOutput(Number(value.type), Amount_1.Amount.reviver("", value.value), Lock_1.Lock.reviver("", value.lock));
    };
    /**
     * Collects data to create a hash.
     * @param buffer The buffer where collected data is stored
     */
    TxOutput.prototype.computeHash = function (buffer) {
        buffer.writeUInt32LE(this.type);
        this.lock.computeHash(buffer);
        this.value.computeHash(buffer);
    };
    /**
     * Converts this object to its JSON representation
     */
    TxOutput.prototype.toJSON = function () {
        return {
            type: this.type,
            value: this.value,
            lock: this.lock,
        };
    };
    /**
     * Returns the data size.
     */
    TxOutput.prototype.getNumberOfBytes = function () {
        return (Utils_1.Utils.SIZE_OF_INT + //  TxOutput.type
            Utils_1.Utils.SIZE_OF_LONG + //  TxOutput.value
            this.lock.getNumberOfBytes()); //  TxOutput.lock
    };
    /**
     * Returns the estimated data size.
     */
    TxOutput.getEstimatedNumberOfBytes = function () {
        return Utils_1.Utils.SIZE_OF_INT + Utils_1.Utils.SIZE_OF_LONG + Utils_1.Utils.SIZE_OF_BYTE + Utils_1.Utils.SIZE_OF_PUBLIC_KEY;
    };
    /**
     * The compare function of TxOutput
     */
    TxOutput.compare = function (a, b) {
        if (a.type !== b.type)
            return a.type < b.type ? -1 : 1;
        if (a.lock.type !== b.lock.type)
            return a.lock.type < b.lock.type ? -1 : 1;
        var comp = Buffer.compare(a.lock.bytes, b.lock.bytes);
        if (comp !== 0)
            return comp;
        return Amount_1.Amount.greaterThan(a.value, b.value) ? 1 : Amount_1.Amount.lessThan(a.value, b.value) ? -1 : 0;
    };
    /**
     * Serialize as binary data.
     * @param buffer - The buffer where serialized data is stored
     */
    TxOutput.prototype.serialize = function (buffer) {
        VarInt_1.VarInt.fromNumber(this.type, buffer, 4);
        this.lock.serialize(buffer);
        VarInt_1.VarInt.fromJSBI(this.value.value, buffer);
    };
    /**
     * Deserialize as binary data.
     * @param buffer - The buffer to be deserialized
     */
    TxOutput.deserialize = function (buffer) {
        var type = VarInt_1.VarInt.toNumber(buffer, 4);
        var lock = Lock_1.Lock.deserialize(buffer);
        var value = VarInt_1.VarInt.toJSBI(buffer);
        return new TxOutput(type, value, lock);
    };
    /**
     * Creates and returns a copy of this object.
     */
    TxOutput.prototype.clone = function () {
        return new TxOutput(this.type, this.value, this.lock.clone());
    };
    Object.defineProperty(TxOutput.prototype, "address", {
        /**
         * If the key type is LockType.Key, return the instance of PublicKey. Otherwise,
         * it returns the instance filled with zero all bytes.
         * @returns The instance of PublicKey
         */
        get: function () {
            if (this.lock.type === Lock_1.LockType.Key) {
                return new KeyPair_1.PublicKey(this.lock.bytes);
            }
            else {
                return KeyPair_1.PublicKey.Null;
            }
        },
        enumerable: false,
        configurable: true
    });
    return TxOutput;
}());
exports.TxOutput = TxOutput;
//# sourceMappingURL=TxOutput.js.map